import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

function Share() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageName = queryParams.get('pageName');
  const eventId = queryParams.get('eventId');
  const code = queryParams.get('code');
  const cityId = queryParams.get('cityId') || queryParams.get('city_id') || queryParams.get('city');

  const [showFallback, setShowFallback] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  /**
   * Copies text to clipboard.
   * Tries the modern async Clipboard API first (Chrome 66+, Safari 13.1+),
   * then falls back to the legacy execCommand approach for older browsers.
   */
  const copyToClipboard = useCallback(async (text) => {
    if (!text) return;

    // Modern Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        console.log('[Referral/DeepLink] Clipboard set via modern API:', text);
        return;
      } catch (e) {
        console.warn('[Referral/DeepLink] Modern clipboard API failed, trying legacy:', e);
      }
    }

    // Legacy fallback (execCommand)
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.top = '0';
      el.style.left = '0';
      el.style.opacity = '0';
      el.setAttribute('readonly', '');
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      console.log('[Referral/DeepLink] Clipboard set via legacy API:', text);
    } catch (e) {
      console.error('[Referral/DeepLink] Clipboard write failed:', e);
    }
  }, []);

  const handleCopyCode = () => {
    copyToClipboard(code);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  useEffect(() => {
    const search = location.search || '';

    if (!search) {
      setShowFallback(true);
      return;
    }

    const query = search.startsWith('?') ? search : `?${search}`;
    const searchParams = search.startsWith('?') ? search.substring(1) : search;

    // ── Priority 0: Copy deep link query / referral code to clipboard IMMEDIATELY ──
    if (searchParams) {
      copyToClipboard(searchParams);
    } else if (code) {
      copyToClipboard(code);
    }

    // ── Priority 1: Attempt to open the app via custom scheme (if installed) ──
    const isProductionHost = window.location.hostname === 'share.outr-connect.com' || window.location.hostname === 'sharing.outr-connect.com';
    const primaryHost = isProductionHost ? window.location.host : 'share.outr-connect.com';
    const fallbackHost = primaryHost === 'sharing.outr-connect.com' ? 'share.outr-connect.com' : 'sharing.outr-connect.com';

    if (pageName || eventId || code || cityId || search) {
      window.location.href = `outrapp://${primaryHost}/share${query}`;

      setTimeout(() => {
        if (document.visibilityState === 'visible') {
          window.location.href = `outrapp://${fallbackHost}/share${query}`;
        }
      }, 500);
    }

    // Show fallback page immediately so user can see details and manually download if deep link fails
    setShowFallback(true);
  }, [location.search, code, pageName, eventId, cityId, copyToClipboard]);

  if (!showFallback) {
    return (
      <div style={{
        backgroundColor: '#0B0D13',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFF',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(235, 78, 35, 0.1)',
            borderTop: '3px solid #EB4E23',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px auto'
          }}></div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          <p style={{ fontSize: '15px', color: '#9CA3AF', fontWeight: '300' }}>Redirecting to Outr App...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="deeplink-page-wrapper">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

        .deeplink-page-wrapper {
          background: radial-gradient(circle at 15% 20%, rgba(235, 78, 35, 0.05) 0%, transparent 45%), 
                      radial-gradient(circle at 85% 80%, rgba(235, 78, 35, 0.04) 0%, transparent 45%), 
                      #F3F4F6;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #1F2937;
          padding: 20px;
          box-sizing: border-box;
        }

        .deeplink-card {
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.9);
          border-radius: 28px;
          padding: 45px 35px;
          max-width: 420px;
          width: 100%;
          text-align: center;
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05), 
                      inset 0 1px 0 rgba(255, 255, 255, 0.8);
          animation: cardAppear 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        @keyframes cardAppear {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .logo-glow-container {
          position: relative;
          width: 100px;
          height: 100px;
          margin: 0 auto 28px auto;
        }

        .logo-glow {
          position: absolute;
          top: -15px;
          left: -15px;
          right: -15px;
          bottom: -15px;
          background: radial-gradient(circle, rgba(235, 78, 35, 0.18) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(8px);
          animation: logoPulse 3s ease-in-out infinite alternate;
        }

        @keyframes logoPulse {
          0% { transform: scale(0.92); opacity: 0.5; }
          100% { transform: scale(1.08); opacity: 0.8; }
        }

        .app-logo-img {
          position: relative;
          width: 100px;
          height: 100px;
          border-radius: 22px;
          border: 1px solid rgba(0, 0, 0, 0.05);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
          object-fit: cover;
        }

        .welcome-title {
          font-size: 26px;
          font-weight: 700;
          margin: 0 0 10px 0;
          letter-spacing: -0.02em;
          color: #111827;
        }

        .welcome-subtitle {
          font-size: 14px;
          color: #4B5563;
          line-height: 1.5;
          margin: 0 0 32px 0;
          font-weight: 400;
        }

        .info-badge {
          background: rgba(235, 78, 35, 0.04);
          border: 1px solid rgba(235, 78, 35, 0.12);
          border-radius: 18px;
          padding: 16px 20px;
          margin-bottom: 32px;
          text-align: left;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .info-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: #EB4E23;
          font-weight: 700;
        }

        .info-value-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .info-value {
          font-size: 15px;
          font-weight: 600;
          color: #1F2937;
          font-family: 'Courier New', Courier, monospace;
          word-break: break-all;
        }

        .copy-btn {
          background: #EB4E23;
          border: none;
          border-radius: 10px;
          color: white;
          padding: 8px 14px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
          font-family: 'Outfit', sans-serif;
        }

        .copy-btn:hover {
          background: #FF5A30;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(235, 78, 35, 0.2);
        }

        .copy-btn:active {
          transform: translateY(0);
        }

        .event-badge {
          background: rgba(0, 0, 0, 0.02);
          border: 1px solid rgba(0, 0, 0, 0.05);
          border-radius: 18px;
          padding: 16px 20px;
          margin-bottom: 32px;
          text-align: left;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .event-label {
          font-size: 10px;
          letter-spacing: 0.12em;
          color: #6B7280;
          font-weight: 500;
        }

        .event-value {
          font-size: 13px;
          color: #4B5563;
          line-height: 1.4;
          font-family: 'Courier New', Courier, monospace;
          word-break: break-all;
        }

        .store-buttons-container {
          display: flex;
          justify-content: center;
          gap: 16px;
          margin-bottom: 36px;
        }

        .store-badge-link {
          transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          border-radius: 10px;
          overflow: hidden;
          display: inline-block;
          line-height: 0;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
        }

        .store-badge-link:hover {
          transform: translateY(-4px) scale(1.03);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
        }

        .store-badge-img {
          width: 145px;
          height: auto;
        }

        .card-footer {
          font-size: 11px;
          color: rgba(0, 0, 0, 0.3);
          letter-spacing: 0.08em;
          font-weight: 500;
          border-top: 1px solid rgba(0, 0, 0, 0.05);
          padding-top: 24px;
        }
      `}</style>

      <div className="deeplink-card">
        <div className="logo-glow-container">
          <div className="logo-glow"></div>
          <img src="/logo.png" alt="Outr App Logo" className="app-logo-img" />
        </div>

        <h1 className="welcome-title">Welcome to Outr</h1>
        <p className="welcome-subtitle">
          Download the app to discover events and connect with others seamlessly!
        </p>

        {code && (
          <div className="info-badge">
            <span className="info-label">Referral Code</span>
            <div className="info-value-container">
              <span className="info-value">{code}</span>
              <button className="copy-btn" onClick={handleCopyCode}>
                {isCopied ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Copied
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {pageName === 'eventDetail' && eventId && (
          <div className="event-badge">
            <span className="event-label">Event Link Detected</span>
            <span className="event-value">{eventId}</span>
          </div>
        )}

        <div className="store-buttons-container">
          {/* Android: Play Store with full referrer param */}
          <a
            href={`https://play.google.com/store/apps/details?id=com.app.outr${
              (location.search && location.search.length > 1)
                ? `&referrer=${encodeURIComponent(location.search.replace(/^\?/, ''))}`
                : (code ? `&referrer=code%3D${encodeURIComponent(code)}` : '')
            }`}
            className="store-badge-link"
          >
            <img src="/play-store.svg" alt="Get on Google Play" className="store-badge-img" />
          </a>

          {/* iOS: Ensure clipboard is (re-)set with deep link parameters when user manually taps the button */}
          <a
            href="https://apps.apple.com/us/app/outr/id6748447370"
            onClick={() => {
              const searchParams = location.search ? location.search.replace(/^\?/, '') : '';
              if (searchParams) {
                copyToClipboard(searchParams);
              } else if (code) {
                copyToClipboard(code);
              }
            }}
            className="store-badge-link"
          >
            <img src="/app-store.svg" alt="Download on App Store" className="store-badge-img" />
          </a>
        </div>

        <div className="card-footer">
          Discover on here, Connect out there
        </div>
      </div>
    </div>
  );
}

export default Share;